import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { SubmitHandler, SubmitErrorHandler, useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import { boolean, number, object, SchemaOf, string, date, } from "yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { DataGrid, GridLinkOperator } from "@mui/x-data-grid";
import { useAuth } from "../../_auth/useAuth";
import {
    Form, FormSectionHeader, FormTitle,
    ButtonBar, DaButton,
    DaAutocompleteField, DaCheckboxField, DaHiddenField, DaTextField,
    DaNumberFormatField,
    DaTabPanel, HTMLComment,
    IMessagePanelProps, MessagePanel, ProgressPanel,
    NumberFormatType,
    DaGridContainer, DaGridLoadingOverlay, DaGridSettings,
    MuiGridDataProp,
    IDialogTextProps, DialogBox, MessageBox, MsgBoxType,
} from "../_shared";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import { AuthRolesEnum, listAdminTypeOptions } from "../../_appData";
import { FormPaper, AevTabs, AevTab } from "./Broker.styles";
import {
    IItemIdRequest,
    IListAdmin,
    IBroker,
    ILookupNAICS,
    ILookupSearchTerm,
    ILookupState,
    brokerApi,
    lookupApi,
    customerApi,
    IUser
} from "../../_api";
import { brokerAEVFields, brokerRelatedPafsColumns } from "../../_appData";
import { throttle } from 'lodash';
import { TAB_FIELDS } from './constant';

const brokerSchema: SchemaOf<IListAdmin> = object({
    listadmin_id: number().integer().optional(),
    listadmin_sid: string().max(10).transform((val, originalVal) => originalVal === null || originalVal === '' ? undefined : val).optional(),
    listadmin_type: number().integer().label(brokerAEVFields.brokerType.label)
        .transform(val => (Number.isNaN(val) ? undefined : val))
        .optional().default(1),
    company_name: string().label(brokerAEVFields.companyName.label).max(128).required(),
    address1: string().label(brokerAEVFields.address1.label).max(80).required(),
    address2: string().label(brokerAEVFields.address2.label).max(80).optional(),
    city: string().label(brokerAEVFields.city.label).max(80).required(),
    state: string().label(brokerAEVFields.state.label).max(5).required(),
    postal_code: string().label(brokerAEVFields.postalCode.label).max(25).required(),
    urbanization: string().label(brokerAEVFields.urbanization.label).max(30).optional(),
    federal_tax_id: string().label(brokerAEVFields.federalTaxId.label).max(30).optional(),
    naics_code: string().label(brokerAEVFields.naicsCode.label).max(10).required(),
    phone: string().label(brokerAEVFields.phone.label).max(25).required(),
    representative_name: string().label(brokerAEVFields.representativeName.label).max(100).required(),
    representative_title: string().label(brokerAEVFields.representativeTitle.label).max(100).optional(),
    title: string().label('Contact Title').max(100).transform((val, originalVal) => originalVal === null || originalVal === '' ? undefined : val).optional(),
    representative_email: string().email().label(brokerAEVFields.representativeEmail.label).max(128).required(),
    company_website: string().label(brokerAEVFields.companyWebsite.label).max(128).optional(),
    notes: string().label(brokerAEVFields.notes.label).max(2048).optional(),
    is_deleted: boolean().label(brokerAEVFields.isDeleted.label).required(),
    create_datetime: date().optional(),
    modify_datetime: date().optional(),
    modify_user_name: string().transform((val) => val === null || val === undefined ? '' : val).optional().default(''),
    approval_status: string().optional(),
    approval_rejection_reason: string().optional(),
    is_foreign_location: boolean().label(brokerAEVFields.isForeignLocation.label).required(),
    is_ncoa: boolean().label(brokerAEVFields.isNcoa.label).required(),
    is_dsf: boolean().label(brokerAEVFields.isDsf.label).required(),
    is_triplex: boolean().label(brokerAEVFields.isTriplex.label).required(),
    is_waiver_on_file: boolean().label(brokerAEVFields.isWaiverOnFile.label).required(),
    is_internal: boolean().label(brokerAEVFields.isInternal.label).required(),
});


export const BrokerAEView: React.FC = () => {
    const theme = useTheme();
    const { user, userHasPrivilege, getAccessToken } = useAuth();
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    // Get navigation state for view mode and context
    const navigationState = location.state as {
        brokerCreationContext?: 'paf' | 'listadmin';
        brokerCreationPafId?: string;
        brokerCreationCustomerId?: number;
        mode?: 'view' | 'partial-edit';
        fromBrokerDashboard?: boolean;
    } | null;

    const isViewModeFromState = navigationState?.mode === 'view';
    const isPartialEditMode = navigationState?.mode === 'partial-edit';
    const isViewModeFromQuery = searchParams.get("mode") === "view";
    const isViewMode = isViewModeFromState || isViewModeFromQuery;
    const fromBrokerDashboard = navigationState?.fromBrokerDashboard;

    const brokerCreationContext = navigationState?.brokerCreationContext;
    const brokerCreationPafId = navigationState?.brokerCreationPafId;
    const customerId = navigationState?.brokerCreationCustomerId;
    const [msgPanelProps, setMsgPanelProps] = useState<IMessagePanelProps>({ message: "" });
    const [msgBoxOpen, setMsgBoxOpen] = useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({ message: "" });
    const [progressTitle, setProgressTitle] = useState<string>("Getting Licensee Info...");
    const [showError, setShowError] = useState<boolean>(false);
    const [showProgress, setShowProgress] = useState<boolean>(true);
    const [selTabIndex, setSelTabIndex] = useState<number>(0);
    const [dlgRejectOpen, setDlgRejectOpen] = useState<boolean>(false);
    const [rejectionReason, setRejectionReason] = useState<string>("");
    const [dlgConfirmOpen, setDlgConfirmOpen] = useState<boolean>(false);
    const [dlgConfirmProps, setDlgConfirmProps] = useState<IDialogTextProps>({ message: "" });
    // const [pageSize, setPageSize] = React.useState<number>(DaGridSettings.defaultPageSize);
    const [stateOptions, setStateOptions] = useState<Array<ILookupState>>([]);
    const [naicsOptions, setNaicsOptions] = useState<Array<ILookupNAICS>>([]);
    const [gridData, setGridData] = useState<MuiGridDataProp>({
        rows: [],
        row_count: 0,
    });

    const formFieldSize = "medium";
    const formFieldVariant = "standard";
    let listAdminId: number | undefined = undefined;

    let aevMode: "add" | "upd" | "err" | undefined = undefined;


    // Validate URL param
    if (params.id) {
        if (Number.isNaN(Number(params.id))) {
            if (params.id !== "new") {
                aevMode = "err";
            } else {
                listAdminId = -1;
                aevMode = "add";
            }
        } else {
            listAdminId = parseInt(params.id, 10);
            aevMode = "upd";
        }
    }

    // defining the initial state for the form
    const initialValues = {
        listadmin_id: -1,
        listadmin_sid: '',
        listadmin_type: -1,
        is_foreign_location: false,
        is_ncoa: false,
        is_dsf: false,
        is_triplex: false,
        is_waiver_on_file: false,
        is_internal: false,
        company_name: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postal_code: '',
        federal_tax_id: '',
        naics_code: '',
        phone: '',
        representative_name: '',
        representative_title: '',
        representative_email: '',
        company_website: '',
        notes: '',
        is_deleted: false,
        create_datetime: new Date(),
        modify_datetime: new Date(),
        modify_user_name: `${user.account_id}`,
        approval_status: '0',
        approval_rejection_reason: '',
    };

    const { control, getValues, handleSubmit, reset, trigger, formState: { errors, isValid } } = useForm<IListAdmin>({
        defaultValues: initialValues,
        resolver: yupResolver(brokerSchema),
        mode: "onChange",
        reValidateMode: "onChange",
    });

    // Edit mode check - in edit mode, only Contact tab is editable
    // Exclude partial-edit mode (creating broker from PAF) from edit restrictions
    const isEditMode = aevMode === "upd" && !isViewMode && !isPartialEditMode;
    const isLicensee = userHasPrivilege([AuthRolesEnum.Licensee]);

    // Tab-specific readonly variables
    // For Licensees: view mode = readonly, edit mode = write mode (all tabs editable)
    // For other roles (ListOwner, Broker, etc.): keep existing logic (List Admin and Address readonly in edit mode, Contact editable)
    const isListAdminTabReadOnly = isLicensee
        ? isViewMode  // Licensee: readonly only in view mode
        : (isEditMode || isViewMode);  // Other roles: readonly in edit mode and view mode
    const isAddressTabReadOnly = isLicensee
        ? isViewMode  // Licensee: readonly only in view mode
        : (isEditMode || isViewMode);  // Other roles: readonly in edit mode and view mode
    const isContactTabReadOnly = isViewMode; // Contact editable in edit mode for all roles

    const stripObjects = (data: any, keys: string[]) =>
        Object.fromEntries(
            Object.entries(data).filter(([key]) => !keys.includes(key))
        );


    const onSubmit: SubmitHandler<IListAdmin> = (data) => {
        // Get current form values to ensure we have the latest updates
        const currentFormData = getValues();
        const comapnyId = (currentFormData as any)?.company?.company_id || '';
        const updateData = stripObjects(currentFormData, ['company', 'user']);
        // Prepare broker data with all form fields
        let brokerData: any;
        // For create operations (add mode or partialEditMode), remove listadmin_id and other fields that backend generates
        if (aevMode === "add" || isPartialEditMode) {
            const { listadmin_id, listadmin_sid, create_datetime, modify_datetime, ...createData } = currentFormData;
            brokerData = {
                ...createData,
                company_type: "1", // List Admin company type
                // Use representative_title as title for ListAdmin, only include if it has a value
                ...(currentFormData.representative_title || currentFormData.title ? { title: currentFormData.representative_title || currentFormData.title } : {}),
                // Ensure approval_status is set (default to pending)
                approval_status: currentFormData.approval_status || '0',
            };
        } else {
            // For update: strip representative_* and user_* keys; send only mapped contact fields (email, title, phone, first_name, last_name, id)
            const keysToStrip = [
                'representative_email', 'representative_name', 'representative_title',
                'user_email', 'user_first_name', 'user_id', 'user_last_name'
            ];
            const updateDataStripped = stripObjects(updateData, keysToStrip);
            const parts = (currentFormData.representative_name || '').trim().split(" ");
            const firstName = parts[0] || '';
            const lastName = parts.slice(1).join(" ").trim();
            brokerData = {
                ...updateDataStripped,
                company_type: "1",
                company_id: comapnyId,
                email: currentFormData.representative_email || '',
                title: currentFormData.representative_title || '',
                phone: currentFormData.phone || '',
                first_name: firstName,
                last_name: lastName,
                id: (currentFormData as any).user_id ?? '',
            };
        }
        saveListAdminData(brokerData);
    };

    const onSubmitErrors: SubmitErrorHandler<IListAdmin> = (errors) => {
        const messages: string[] = [];

        const walk = (obj: any) => {
            Object.values(obj).forEach((val: any) => {
                if (val?.message) messages.push(val.message);
                if (val && typeof val === "object") walk(val);
            });
        };

        walk(errors);

        msgBox_Show(
            `Please fix the following required fields:\n\n${messages
                .map((m) => `• ${m}`)
                .join("\n")}`,
            "Invalid Broker Info",
            "error"
        );
    };

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    };

    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        });
        setMsgBoxOpen(true);
    };


    const handleTabChange = (e: React.SyntheticEvent, idx: number) => {
        setSelTabIndex(idx);
    }

    const handleNext = async () => {
        const fields = TAB_FIELDS[selTabIndex];

        if (!fields || fields.length === 0) {
            setSelTabIndex((prev) => prev + 1);
            return;
        }

        const isTabValid = await trigger(fields, {
            shouldFocus: true,
        });

        if (!isTabValid) return;

        setSelTabIndex((prev) => prev + 1);
    };

    const handlePrevious = () => {
        setSelTabIndex((prev) => prev - 1);
    };

    const dlgConfirm_Close = (value?: string) => {
        setDlgConfirmOpen(false);
        if (value && value === "ok") {
            handleApprove();
        }
    }

    const dlgConfirm_Show = (type: 'reject' | 'approve') => {
        setDlgConfirmProps({
            message: type === 'reject' ? "Are you sure you want to reject this Broker?" : "Are you sure you want to approve this Broker?",
            title: type === 'reject' ? "Reject Broker" : "Approve Broker",
        })
        if (type === 'reject') {
            setDlgRejectOpen(true);
        } else {
            setDlgConfirmOpen(true);
        }
    }

    const dlgReject_Close = () => {
        if (!rejectionReason.trim()) {
            return;
        }

        const formData = getValues();
        const rejectionData = {
            ...formData,
            approval_status: 2, // Rejected status
            approval_rejection_reason: rejectionReason,
        };

        saveListAdminData(rejectionData);
        setDlgRejectOpen(false);
        setRejectionReason('');
    }

    const handleApprove = () => {
        const formData = getValues();
        const approvalData = {
            ...formData,
            approval_status: 1, // Approved status
            approval_rejection_reason: '',
        };

        saveListAdminData(approvalData);
        setDlgConfirmOpen(false);
    }

    const saveListAdminData = useCallback(async (options): Promise<void> => {
        // Set progress title based on mode
        if (isPartialEditMode) {
            setProgressTitle("Adding Broker...");
        } else if (aevMode === "add") {
            setProgressTitle("Creating Broker...");
        } else if (aevMode === "upd") {
            setProgressTitle("Saving Broker...");
        } else {
            setProgressTitle("Saving Broker Info...");
        }
        setShowProgress(true);

        const auth_token = await getAccessToken();
        let apiPromise = undefined;

        // Clean up the data before sending - remove undefined values and ensure all required fields are present
        const cleanOptions: IBroker = Object.fromEntries(
            Object.entries(options).filter(([_, value]) => value !== undefined)
        ) as unknown as IBroker;

        // In partial-edit mode or add mode, always create a new broker (not update)
        if (aevMode === "add" || isPartialEditMode) {
            // For create operations, ensure listadmin_id is not sent
            const { listadmin_id, ...createData } = cleanOptions;
            apiPromise = brokerApi.add(createData, auth_token);
        } else if (aevMode === "upd") {
            // For update operations, listadmin_id is required
            apiPromise = brokerApi.update(cleanOptions, auth_token);
        } else {
            msgBox_Show(
                "An error occurred while saving Broker data. " +
                "Navigate to the Broker list view and retry your last action.",
                "Inconsistent Form State Error",
                "error"
            );
            setShowProgress(false);
            return;
        }

        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.success) {
                // Navigate based on context from navigation state
                if (brokerCreationContext === 'paf' && brokerCreationPafId) {
                    console.log('response data: ', resp.data);
                    // Pass the newly created broker SID back to PAF
                    const newBrokerSid = resp.data?.listadmin_sid;
                    navigate(`/paf/${brokerCreationPafId}`, {
                        state: {
                            newBrokerSid: newBrokerSid,
                            navigateToTab: 3 // List Admin tab
                        }
                    });
                } else if (brokerCreationContext === 'listadmin') {
                    // Coming from listadmin dashboard, return to dashboard
                    navigate("/brokers");
                } else {
                    // Default: go to listadmin dashboard
                    navigate("/brokers");
                }
            } else {
                setMsgBoxProps({
                    title: "Unable to Save Broker",
                    message: "An error occurred while saving Broker data. Verify that you have provided all required data and retry your last action.",
                    type: "error"
                });
                setMsgBoxOpen(true);
            }
        })
            .catch(() => {
                setMsgBoxProps({
                    title: "Broker API Save Error",
                    message: "An error occurred while saving Broker data. There may have been a network error.",
                    type: "error"
                });
                setMsgBoxOpen(true);
            })
            .finally(() => {
                setShowProgress(false);
            });
    }, [brokerCreationContext, brokerCreationPafId]);


    // Lookup Field methods
    const stateSearch = React.useRef(
        throttle(async (search_term) => {
            if (search_term.length < 2) {
                setStateOptions([]);
            } else {
                setStateOptions(await getStateOptions(search_term) as Array<ILookupState>);
            }
        }, 250)
    ).current;

    const stateInputChanged = async (e: React.SyntheticEvent, newValue: string, reason: string): Promise<void> => {
        // Don't re-run search for
        // programmatic change, user selection
        if (reason !== "reset") {
            stateSearch(newValue);
        }
    }

    const getStateOptions = async (search_term: string): Promise<Array<ILookupState> | void> => {
        const options: ILookupSearchTerm = { search_term: search_term };
        const auth_token = await getAccessToken();
        try {
            const resp = await lookupApi.get_state_lookup(options, auth_token);
            if (resp) {
                if (resp.success) {
                    return resp.data;
                } else if (resp.error && resp.error.length > 0) {
                    msgBox_Show(
                        "An error occurred while retrieving State lookups data. " +
                        "The State lookups data was not found.",
                        "State lookups Not Found Error",
                        "error"
                    );
                }
            }
        }
        catch (err) {
            msgBox_Show(
                "An error occurred while fetching State lookups data. " +
                "There may have been a network error.",
                "Lookup API Fetch Error",
                "error"
            );
        }
    };

    const naicsSearch = React.useRef(
        throttle(async (search_term) => {
            if (search_term.length < 3) {
                setNaicsOptions([]);
            } else {
                setNaicsOptions(await getNaicsOptions(search_term) as Array<ILookupNAICS>);
            }
        }, 250)
    ).current;

    const naicsInputChanged = async (e: React.SyntheticEvent, newValue: string, reason: string): Promise<void> => {
        // Don't re-run search for
        // programmatic change, user selection
        if (reason !== "reset") {
            naicsSearch(newValue);
        }
    }

    const getNaicsOptions = async (search_term: string): Promise<Array<ILookupNAICS> | void> => {
        const options: ILookupSearchTerm = { search_term: search_term };
        const auth_token = await getAccessToken();
        try {
            const resp = await lookupApi.get_naics_lookup(options, auth_token);
            if (resp) {
                if (resp.success) {
                    return resp.data;
                } else if (resp.error && resp.error.length > 0) {
                    msgBox_Show(
                        "An error occurred while retrieving NAICS lookups data. " +
                        "The NAICS lookups data was not found.",
                        "NAICS lookups Not Found Error",
                        "error"
                    );
                }
            }
        }
        catch (err) {
            msgBox_Show(
                "An error occurred while fetching NAICS lookups data. " +
                "There may have been a network error.",
                "Lookup API Fetch Error",
                "error"
            );
        }
    };

    const getListAdminData = useCallback(async (): Promise<void> => {
        setProgressTitle("Getting Broker Data...");
        setShowProgress(true);

        if (aevMode === "err") {
            msgBox_Show(
                "An error occurred while retrieving Broker data. " +
                "Please go to the Broker list view and retry your latest action.",
                "Invalid Broker ID",
                "error"
            );
            setShowProgress(false);
        } else if (aevMode === "add") {
            // New ListAdmin - reset form to initial values
            reset(initialValues);
            setShowProgress(false);
        } else if (aevMode === "upd") {
            const auth_token = await getAccessToken();

            // In partial-edit mode, call customerApi.get_by_id
            // In edit/view mode, call brokerApi.get_by_id
            if (isPartialEditMode) {
                // First get broker data to get broker-specific fields (listadmin_id, listadmin_sid, notes)
                const brokerOptions: IItemIdRequest = { id: listAdminId as number };
                customerApi.get_broker_add_get_details(brokerOptions, auth_token).then((brokerResp) => {
                    if (brokerResp && brokerResp.success) {
                        const brokerData = brokerResp.data;
                        const companyData = brokerData;
                        const flatData = {
                            listadmin_id: brokerData.listadmin_id,
                            listadmin_sid: brokerData.listadmin_sid,
                            listadmin_type: brokerData.listadmin_type || 1,
                            company_name: companyData.company_name || '',
                            is_foreign_location: companyData.is_foreign_location || false,
                            is_ncoa: companyData.is_ncoa || false,
                            is_dsf: companyData.is_dsf || false,
                            is_triplex: companyData.is_triplex || false,
                            is_waiver_on_file: companyData.is_waiver_on_file || false,
                            is_internal: companyData.is_internal || false,
                            address1: companyData.address1 || '',
                            address2: companyData.address2 || '',
                            city: companyData.city || '',
                            state: companyData.state || '',
                            postal_code: companyData.postal_code || '',
                            urbanization: companyData.urbanization || '',
                            federal_tax_id: companyData.federal_tax_id || '',
                            naics_code: companyData.naics_code || '',
                            // phone: companyData.phone || '',
                            // representative_name: companyData.representative_name || '',
                            // representative_title: companyData.representative_title || '',
                            // representative_email: companyData.representative_email || '',
                            // company_website: companyData.company_website || '',
                            notes: brokerData.notes || '',
                            is_deleted: companyData.is_deleted || false,
                            create_datetime: companyData.create_datetime,
                            modify_datetime: companyData.modify_datetime,
                            modify_user_name: companyData.modify_user_name || '',
                        };

                        reset(flatData);
                        setShowProgress(false);


                    } else {
                        msgBox_Show(
                            "An error occurred while retrieving Broker data. " +
                            "Please go to the Broker list view and retry your latest action.",
                            "Invalid Broker ID",
                            "error"
                        );
                        setShowProgress(false);
                    }
                }).catch(() => {
                    msgBox_Show(
                        "An error occurred while retrieving Broker data. " +
                        "Please go to the Broker list view and retry your latest action.",
                        "Invalid Broker ID",
                        "error"
                    );
                    setShowProgress(false);
                })
                    .finally(() => {
                        const fieldValues = getValues(["naics_code", "state"]);   // "naics_code"
                        naicsSearch(fieldValues[0]);
                        stateSearch(fieldValues[1]);
                    });
            } else {
                // Edit or view mode - call brokerApi.get_by_id
                const brokerOptions: IItemIdRequest = { id: listAdminId as number };
                brokerApi.get_by_id(brokerOptions, auth_token).then((resp) => {
                    if (resp) {
                        if (resp.success) {
                            if (resp.data.related_pafs) {
                                setGridData({
                                    rows: resp.data.related_pafs,
                                    row_count: resp.data.related_pafs.length,
                                });
                            }

                            // Flatten company and user fields into main form data
                            const brokerData = resp.data;
                            const company = brokerData.company || {};
                            const user = brokerData.user || {};
                            // Use user object for contact fields
                            const contact_name = user.first_name || user.last_name ? `${user.first_name} ${user.last_name}` : '';
                            const contact_email = user.email || '';
                            const contact_title = brokerData.title || '';
                            const flatData = {
                                ...brokerData,
                                company_name: company.company_name || '',
                                is_foreign_location: company.is_foreign_location || false,
                                is_ncoa: company.is_ncoa || false,
                                is_dsf: company.is_dsf || false,
                                is_triplex: company.is_triplex || false,
                                is_waiver_on_file: company.is_waiver_on_file || false,
                                is_internal: company.is_internal || false,
                                address1: company.address1 || '',
                                address2: company.address2 || '',
                                city: company.city || '',
                                state: company.state || '',
                                postal_code: company.postal_code || '',
                                urbanization: company.urbanization || '',
                                federal_tax_id: company.federal_tax_id || '',
                                naics_code: company.naics_code || '',
                                phone: user.phone || company.phone || '',
                                representative_name: contact_name,
                                representative_title: contact_title,
                                representative_email: contact_email,
                                company_website: company.company_website || '',
                                is_deleted: company.is_deleted || false,
                                create_datetime: company.create_datetime || company.create_datetime,
                                modify_datetime: company.modify_datetime || company.modify_datetime,
                                modify_user_name: company.modify_user_name || '',
                                user_id: user.id || '',
                                user_email: user.email || '',
                                user_first_name: user.first_name || '',
                                user_last_name: user.last_name || '',
                                notes: company.notes || '',
                            };

                            reset(flatData);
                            setShowProgress(false);
                        } else {
                            msgBox_Show(
                                "An error occurred while retrieving Broker data. " +
                                "Please go to the Broker list view and retry your latest action.",
                                "Invalid Broker ID",
                                "error"
                            );
                            setShowProgress(false);
                        }
                    }
                }).catch(() => {
                    msgBox_Show(
                        "An error occurred while retrieving Broker data. " +
                        "Please go to the Broker list view and retry your latest action.",
                        "Invalid Broker ID",
                        "error"
                    );
                    setShowProgress(false);
                }).finally(() => {
                    const fieldValues = getValues(["naics_code", "state"]);   // "naics_code"
                    naicsSearch(fieldValues[0]);
                    stateSearch(fieldValues[1]);
                });
            }
        }

    }, [listAdminId, aevMode]); // Removed 'reset' from dependencies as it's stable from react-hook-form

    useEffect(() => {
        // Get data from remote service
        const resp = getListAdminData();

        return () => {
            naicsSearch.cancel();
            stateSearch.cancel();
        };
    }, [getListAdminData, naicsSearch, stateSearch]);


    return (
        <>
            {listAdminId &&
                <>
                    <FormPaper elevation={4}>
                        {/* Form Title*/}
                        <FormTitle
                            title={
                                isPartialEditMode
                                    ? "Brokers : Add"
                                    : aevMode === "add"
                                        ? "Brokers : New "
                                        : "Brokers : Update"
                            }
                            titleIcon={"group_work_rounded_icon"}
                        />

                        <Grid container>
                            {showProgress ? (
                                <Grid container justifyContent={"center"} item>
                                    <ProgressPanel title={progressTitle} />
                                </Grid>
                            ) : (showError ? (
                                <Grid container justifyContent={"center"} item>
                                    <MessagePanel
                                        {...msgPanelProps}
                                    />
                                </Grid>
                            ) : (
                                <Grid item xs={12}>
                                    <Form
                                        autoComplete="off"
                                        onSubmit={handleSubmit(onSubmit, onSubmitErrors)}
                                    >
                                        <AevTabs value={selTabIndex} onChange={handleTabChange}>
                                            <AevTab label="Company" />
                                            <AevTab label="Address" />
                                            <AevTab label="Contact" />
                                            {!isPartialEditMode && aevMode !== "add" && <AevTab label="PAFs" />}
                                        </AevTabs>
                                        <Box sx={{ minHeight: theme.spacing(60) }}>
                                            <DaTabPanel selectedIndex={selTabIndex} index={0}>
                                                <Box sx={{ pointerEvents: isListAdminTabReadOnly ? 'none' : 'auto' }}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        {/* List Admin Section Header */}
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Company Info"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        {!isPartialEditMode && (
                                                            <Grid item xs={12} md={12}>
                                                                <DaTextField
                                                                    control={control}
                                                                    name={brokerAEVFields.brokerSid.name}
                                                                    label={brokerAEVFields.brokerSid.label}
                                                                    readOnly
                                                                    variant={formFieldVariant}
                                                                    size={formFieldSize}
                                                                    errors={errors}
                                                                />
                                                            </Grid>
                                                        )}
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={brokerAEVFields.companyName.name}
                                                                label={brokerAEVFields.companyName.label}
                                                                required={brokerAEVFields.companyName.required}
                                                                readOnly={isListAdminTabReadOnly || isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaAutocompleteField
                                                                control={control}
                                                                name={brokerAEVFields.naicsCode.name}
                                                                label={brokerAEVFields.naicsCode.label}
                                                                required={brokerAEVFields.naicsCode.required}
                                                                readOnly={isListAdminTabReadOnly || isPartialEditMode}
                                                                options={naicsOptions.map(({ naics_code, naics_title }) =>
                                                                    ({ value: naics_code, label: `${naics_code} - ${naics_title}` })
                                                                )}
                                                                onInputChange={naicsInputChanged}
                                                                noOptionsText="Type your industry category to find the NAICS"
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaNumberFormatField
                                                                control={control}
                                                                name={brokerAEVFields.federalTaxId.name}
                                                                label={brokerAEVFields.federalTaxId.label}
                                                                required={brokerAEVFields.federalTaxId.required}
                                                                readOnly={isListAdminTabReadOnly || isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                numberFormat={NumberFormatType.EIN}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaAutocompleteField
                                                                control={control}
                                                                name={brokerAEVFields.brokerType.name}
                                                                label={brokerAEVFields.brokerType.label}
                                                                required={brokerAEVFields.brokerType.required}
                                                                readOnly={isListAdminTabReadOnly || isPartialEditMode}
                                                                options={listAdminTypeOptions.map(({ value, text }) =>
                                                                    ({ value: value, label: `${text}` })
                                                                )}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={brokerAEVFields.isForeignLocation.name}
                                                                label={brokerAEVFields.isForeignLocation.label}
                                                                disabled={isListAdminTabReadOnly || isPartialEditMode}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={brokerAEVFields.isNcoa.name}
                                                                label={brokerAEVFields.isNcoa.label}
                                                                disabled={isListAdminTabReadOnly || isPartialEditMode}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={brokerAEVFields.isDsf.name}
                                                                label={brokerAEVFields.isDsf.label}
                                                                disabled={isListAdminTabReadOnly || isPartialEditMode}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={brokerAEVFields.isTriplex.name}
                                                                label={brokerAEVFields.isTriplex.label}
                                                                disabled={isListAdminTabReadOnly || isPartialEditMode}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={brokerAEVFields.isWaiverOnFile.name}
                                                                label={brokerAEVFields.isWaiverOnFile.label}
                                                                disabled={isListAdminTabReadOnly || isPartialEditMode}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={brokerAEVFields.isInternal.name}
                                                                label={brokerAEVFields.isInternal.label}
                                                                disabled={isListAdminTabReadOnly || isPartialEditMode}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={brokerAEVFields.notes.name}
                                                                label={brokerAEVFields.notes.label}
                                                                required={brokerAEVFields.notes.required}
                                                                readOnly={isListAdminTabReadOnly || isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                multiline
                                                                rows={4}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaHiddenField
                                                                control={control}
                                                                name={brokerAEVFields.createDatetime.name}
                                                                errors={errors}
                                                            />
                                                            <DaHiddenField
                                                                control={control}
                                                                name={brokerAEVFields.brokerId.name}
                                                                errors={errors}
                                                            />
                                                            <DaHiddenField
                                                                control={control}
                                                                name={brokerAEVFields.isDeleted.name}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </DaTabPanel>
                                            <DaTabPanel selectedIndex={selTabIndex} index={1}>
                                                <Box sx={{ pointerEvents: isAddressTabReadOnly ? 'none' : 'auto' }}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Address"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={brokerAEVFields.address1.name}
                                                                label={brokerAEVFields.address1.label}
                                                                required={brokerAEVFields.address1.required}
                                                                readOnly={isAddressTabReadOnly || isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={brokerAEVFields.address2.name}
                                                                label={brokerAEVFields.address2.label}
                                                                required={brokerAEVFields.address2.required}
                                                                readOnly={isAddressTabReadOnly || isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <DaTextField
                                                                control={control}
                                                                name={brokerAEVFields.city.name}
                                                                label={brokerAEVFields.city.label}
                                                                required={brokerAEVFields.city.required}
                                                                readOnly={isAddressTabReadOnly || isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaAutocompleteField
                                                                control={control}
                                                                name={brokerAEVFields.state.name}
                                                                label={brokerAEVFields.state.label}
                                                                required={brokerAEVFields.state.required}
                                                                readOnly={isAddressTabReadOnly || isPartialEditMode}
                                                                options={stateOptions.map(
                                                                    ({ state_code, state_name, country_code }) =>
                                                                        ({ value: state_code, label: `${state_code} - ${state_name} (${country_code})` })
                                                                )}
                                                                onInputChange={stateInputChanged}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={2}>
                                                            <DaTextField
                                                                control={control}
                                                                name={brokerAEVFields.postalCode.name}
                                                                label={brokerAEVFields.postalCode.label}
                                                                required={brokerAEVFields.postalCode.required}
                                                                readOnly={isAddressTabReadOnly || isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <DaTextField
                                                                control={control}
                                                                name={brokerAEVFields.urbanization.name}
                                                                label={brokerAEVFields.urbanization.label}
                                                                required={brokerAEVFields.urbanization.required}
                                                                readOnly={isAddressTabReadOnly || isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <HTMLComment text={"Placeholder"} />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </DaTabPanel>
                                            <DaTabPanel selectedIndex={selTabIndex} index={2}>
                                                <Box sx={{ pointerEvents: isContactTabReadOnly ? 'none' : 'auto' }}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Contact Info"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={brokerAEVFields.representativeName.name}
                                                                label={brokerAEVFields.representativeName.label}
                                                                required={brokerAEVFields.representativeName.required}
                                                                readOnly={isContactTabReadOnly && !isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={brokerAEVFields.representativeTitle.name}
                                                                label={brokerAEVFields.representativeTitle.label}
                                                                required={brokerAEVFields.representativeTitle.required}
                                                                readOnly={isContactTabReadOnly && !isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={brokerAEVFields.representativeEmail.name}
                                                                label={brokerAEVFields.representativeEmail.label}
                                                                required={brokerAEVFields.representativeEmail.required}
                                                                readOnly={isEditMode || (isContactTabReadOnly && !isPartialEditMode)}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={brokerAEVFields.companyWebsite.name}
                                                                label={brokerAEVFields.companyWebsite.label}
                                                                required={brokerAEVFields.companyWebsite.required}
                                                                readOnly={isViewMode && !isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaNumberFormatField
                                                                control={control}
                                                                name={brokerAEVFields.phone.name}
                                                                label={brokerAEVFields.phone.label}
                                                                required={brokerAEVFields.phone.required}
                                                                readOnly={isViewMode && !isPartialEditMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                numberFormat={NumberFormatType.US_Phone}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </DaTabPanel>
                                            <DaTabPanel selectedIndex={selTabIndex} index={3}>
                                                <Grid container spacing={theme.spacing(1)}>
                                                    <Grid item xs={12} md={12}>
                                                        <FormSectionHeader
                                                            title={"Related PAFs"}
                                                            typeVariant={"h5"}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={12}>
                                                        <DaGridContainer
                                                            sx={{
                                                                height: 430,
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <DataGrid
                                                                components={{
                                                                    LoadingOverlay: DaGridLoadingOverlay,
                                                                    // Toolbar: lvGridToolbar,
                                                                }}
                                                                initialState={{
                                                                    pagination: {
                                                                        page: 0,
                                                                        pageSize: DaGridSettings.defaultPageSize,
                                                                    },
                                                                }}
                                                                columns={brokerRelatedPafsColumns}
                                                                rows={gridData.rows}
                                                                rowCount={gridData.row_count}
                                                                getRowId={(row) => row.paf_id}
                                                                rowsPerPageOptions={DaGridSettings.rowsPerPageOptions}
                                                                headerHeight={DaGridSettings.headerRowHeight}
                                                                rowHeight={DaGridSettings.rowHeight}
                                                                disableSelectionOnClick
                                                            />
                                                        </DaGridContainer>
                                                    </Grid>
                                                </Grid>
                                            </DaTabPanel>
                                        </Box>
                                        <Grid container spacing={theme.spacing(1)}>
                                            <Grid item xs={12} md={12}>
                                                {/*<ListAdminTypeWatched control={control} />*/}
                                                <ButtonBar buttonAlignment="alignRight">
                                                    {/* Partial edit mode from broker dropdown */}
                                                    {isPartialEditMode && fromBrokerDashboard && (
                                                        <>
                                                            <DaButton buttonStyle="secondary" onClick={() => { navigate("/brokers") }}>
                                                                Cancel
                                                            </DaButton>
                                                            {/* Navigation buttons for tabs 0-1 */}
                                                            {selTabIndex < 2 && (
                                                                <>
                                                                    {selTabIndex > 0 && (
                                                                        <DaButton buttonStyle="secondary" onClick={handlePrevious}>
                                                                            Previous
                                                                        </DaButton>
                                                                    )}
                                                                    <DaButton buttonStyle="primary" onClick={handleNext}>
                                                                        Next
                                                                    </DaButton>
                                                                </>
                                                            )}
                                                            {/* Add and Create New buttons on last tab (tab 2 - Contact) */}
                                                            {selTabIndex === 2 && (
                                                                <>
                                                                    <DaButton buttonStyle="primary" onClick={handleSubmit(onSubmit, onSubmitErrors)}>
                                                                        Add Broker
                                                                    </DaButton>

                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                    {/* View mode from ListAdmin Dashboard */}
                                                    {isViewMode && fromBrokerDashboard && !isPartialEditMode && (
                                                        <>
                                                            <DaButton buttonStyle="secondary" onClick={() => { navigate("/brokers") }}>
                                                                Cancel
                                                            </DaButton>
                                                            {/* Navigation buttons for tabs 0-2 */}
                                                            {selTabIndex < 3 && (
                                                                <>
                                                                    {selTabIndex > 0 && (
                                                                        <DaButton buttonStyle="secondary" onClick={handlePrevious}>
                                                                            Previous
                                                                        </DaButton>
                                                                    )}
                                                                    <DaButton buttonStyle="primary" onClick={handleNext}>
                                                                        Next
                                                                    </DaButton>
                                                                </>
                                                            )}
                                                            {/* Cancel, Add, Create buttons on last tab (tab 3) */}
                                                            {selTabIndex === 3 && (
                                                                <>
                                                                    <DaButton buttonStyle="primary" onClick={() => { navigate("/brokers") }}>
                                                                        Add Broker
                                                                    </DaButton>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                    {/* Normal view/edit mode buttons */}
                                                    {!fromBrokerDashboard && (
                                                        <>
                                                            <DaButton buttonStyle="secondary" onClick={() => {
                                                                if (brokerCreationContext === 'paf' && brokerCreationPafId && aevMode === "add") {
                                                                    navigate(`/paf/${brokerCreationPafId}`, {
                                                                        state: {
                                                                            navigateToTab: 3 // List Admin tab
                                                                        }
                                                                    });
                                                                } else {
                                                                    navigate("/brokers");
                                                                }
                                                            }

                                                            }>
                                                                {brokerCreationContext === 'listadmin' && aevMode === "add" ? "Cancel" : "Close"}
                                                            </DaButton>
                                                            {/* Determine last tab index based on mode */}
                                                            {(() => {
                                                                const lastTabIndex = aevMode === "add" ? 2 : 3;

                                                                return (
                                                                    <>
                                                                        {/* Navigation buttons for all tabs except last */}
                                                                        {selTabIndex < lastTabIndex && (
                                                                            <>
                                                                                {selTabIndex > 0 && (
                                                                                    <DaButton buttonStyle="secondary" onClick={handlePrevious}>
                                                                                        Previous
                                                                                    </DaButton>
                                                                                )}
                                                                                <DaButton buttonStyle="primary" onClick={handleNext}>
                                                                                    Next
                                                                                </DaButton>
                                                                            </>
                                                                        )}
                                                                        {/* Approve/Reject buttons on last tab in view mode */}
                                                                        {selTabIndex === lastTabIndex && isViewMode && userHasPrivilege([AuthRolesEnum.SystemAdmin, AuthRolesEnum.PafAdmin]) && (
                                                                            <>
                                                                                <DaButton buttonStyle="secondary" onClick={() => { dlgConfirm_Show('reject') }}>
                                                                                    Reject
                                                                                </DaButton>
                                                                                <DaButton buttonStyle="primary" onClick={() => { dlgConfirm_Show('approve') }}>
                                                                                    Approve
                                                                                </DaButton>
                                                                            </>
                                                                        )}
                                                                        {/* Save button on last tab in edit/create mode */}
                                                                        {selTabIndex === lastTabIndex && !isViewMode && userHasPrivilege([AuthRolesEnum.ListAdmin, AuthRolesEnum.ListOwner, AuthRolesEnum.Licensee]) && (
                                                                            <DaButton buttonStyle="primary" type="submit">
                                                                                Save Broker
                                                                            </DaButton>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </>
                                                    )}
                                                </ButtonBar>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                </Grid>
                            )
                            )}
                        </Grid>
                    </FormPaper>

                    {/* Rejection Dialog */}
                    <Dialog open={dlgRejectOpen} onClose={() => setDlgRejectOpen(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>Reject Broker</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Rejection Reason"
                                fullWidth
                                multiline
                                rows={4}
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                required
                                helperText="Please provide a reason for rejection"
                            />
                        </DialogContent>
                        <DialogActions>
                            <DaButton buttonStyle="secondary" onClick={() => { setDlgRejectOpen(false); setRejectionReason(''); }}>
                                Cancel
                            </DaButton>
                            <DaButton
                                buttonStyle="primary"
                                onClick={dlgReject_Close}
                                disabled={!rejectionReason.trim()}
                            >
                                Reject
                            </DaButton>
                        </DialogActions>
                    </Dialog>

                    {/* Approval Confirmation Dialog */}
                    <DialogBox
                        isOpen={dlgConfirmOpen}
                        onClose={dlgConfirm_Close}
                        title={dlgConfirmProps.title}
                        message={dlgConfirmProps.message}
                    />

                    {/* Validation Error Message Box */}
                    <MessageBox
                        isOpen={msgBoxOpen}
                        onClose={msgBox_Close}
                        {...msgBoxProps}
                    />
                </>
            }
        </>
    );
}